import { useLayoutEffect, useRef, type RefObject } from 'react';
import { computeVisualScale } from '../utils/contentZoomTransform';

export type PinchZoomOptions = {
  enabled: boolean;
  /** Viewport DOM’a bağlandığında effect’i yeniden çalıştır (koşullu render / portal) */
  mountKey?: string | number | boolean;
  minZoom: number;
  maxZoom: number;
  getZoom: () => number;
  setZoom: (zoom: number) => void;
  getPan: () => { x: number; y: number };
  setPan: (pan: { x: number; y: number }) => void;
  getLayout?: () => { baseW: number; viewportW: number } | null;
  onPinchStart?: () => void;
  onPinchEnd?: () => void;
};

type PinchState = {
  initialDistance: number;
  initialZoom: number;
  initialVisualScale: number;
  initialPan: { x: number; y: number };
  focalX: number;
  focalY: number;
};

type Pt = { x: number; y: number };

/** Masaüstü Option/Alt pinch için sanal 2. parmak id’si */
const ALT_MIRROR_POINTER_ID = -1;

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function center(a: Pt, b: Pt): Pt {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

/**
 * Pinch zoom:
 * - Touch / Pointer (iOS + Simulator)
 * - Masaüstü: Option/Alt + sürükle → merkeze göre ayna parmak (Simulator UX)
 * - gesture*: yalnızca Safari sayfa zoom’unu engeller
 */
export function usePinchZoom(
  elementRef: RefObject<HTMLElement | null>,
  options: PinchZoomOptions,
) {
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const pinchRef = useRef<PinchState | null>(null);
  const isPinchingRef = useRef(false);
  const { enabled, mountKey } = options;

  useLayoutEffect(() => {
    const el = elementRef.current;
    if (!el || !enabled) return;

    const pointers = new Map<number, Pt>();
    let altMirrorActive = false;

    const focalFromViewport = (clientX: number, clientY: number) => {
      const rect = el.getBoundingClientRect();
      return {
        x: clientX - rect.left - rect.width / 2,
        y: clientY - rect.top - rect.height / 2,
      };
    };

    const mirrorAroundViewportCenter = (p: Pt): Pt => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      return { x: 2 * cx - p.x, y: 2 * cy - p.y };
    };

    const beginPinch = (a: Pt, b: Pt) => {
      const opts = optionsRef.current;
      if (!opts.enabled) return false;
      const d = dist(a, b);
      if (d < 8) return false;

      const c = center(a, b);
      const focal = focalFromViewport(c.x, c.y);
      const layout = opts.getLayout?.();
      const initialZoom = opts.getZoom();
      const initialVisualScale = layout
        ? computeVisualScale(initialZoom, layout.baseW, layout.viewportW)
        : initialZoom;

      pinchRef.current = {
        initialDistance: d,
        initialZoom,
        initialVisualScale,
        initialPan: { ...opts.getPan() },
        focalX: focal.x,
        focalY: focal.y,
      };
      isPinchingRef.current = true;
      opts.onPinchStart?.();
      return true;
    };

    const applyFromPoints = (a: Pt, b: Pt) => {
      const opts = optionsRef.current;
      const pinch = pinchRef.current;
      if (!opts.enabled || !pinch || pinch.initialDistance < 8) return;

      const scale = dist(a, b) / pinch.initialDistance;
      if (!(scale > 0) || !Number.isFinite(scale)) return;

      const nextZoom = Math.max(
        opts.minZoom,
        Math.min(opts.maxZoom, pinch.initialZoom * scale),
      );
      const layout = opts.getLayout?.();
      const nextVisualScale = layout
        ? computeVisualScale(nextZoom, layout.baseW, layout.viewportW)
        : nextZoom;
      const ratio =
        pinch.initialVisualScale > 0
          ? nextVisualScale / pinch.initialVisualScale
          : nextZoom / pinch.initialZoom;

      opts.setZoom(nextZoom);
      opts.setPan({
        x: pinch.initialPan.x + pinch.focalX * (1 - ratio),
        y: pinch.initialPan.y + pinch.focalY * (1 - ratio),
      });
    };

    const finishPinch = () => {
      const was = isPinchingRef.current;
      pinchRef.current = null;
      isPinchingRef.current = false;
      altMirrorActive = false;
      if (was) optionsRef.current.onPinchEnd?.();
    };

    const twoPoints = (): [Pt, Pt] | null => {
      if (pointers.size < 2) return null;
      const pts = Array.from(pointers.values());
      return [pts[0], pts[1]];
    };

    const syncPinchFromPointers = () => {
      const pair = twoPoints();
      if (!pair) {
        if (isPinchingRef.current) finishPinch();
        return;
      }
      if (!isPinchingRef.current) beginPinch(pair[0], pair[1]);
      else applyFromPoints(pair[0], pair[1]);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (!optionsRef.current.enabled) return;

      // Masaüstü: Option/Alt + tık → Simulator’daki gibi iki parmak
      if (e.pointerType === 'mouse') {
        if (!e.altKey || e.button !== 0) return;
        e.preventDefault();
        const p = { x: e.clientX, y: e.clientY };
        pointers.clear();
        pointers.set(e.pointerId, p);
        pointers.set(ALT_MIRROR_POINTER_ID, mirrorAroundViewportCenter(p));
        altMirrorActive = true;
        try {
          el.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        syncPinchFromPointers();
        return;
      }

      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size >= 2) {
        e.preventDefault();
        syncPinchFromPointers();
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (altMirrorActive && e.pointerType === 'mouse') {
        if (!pointers.has(e.pointerId)) return;
        e.preventDefault();
        const p = { x: e.clientX, y: e.clientY };
        pointers.set(e.pointerId, p);
        pointers.set(ALT_MIRROR_POINTER_ID, mirrorAroundViewportCenter(p));
        syncPinchFromPointers();
        return;
      }

      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size >= 2 && isPinchingRef.current) {
        e.preventDefault();
        syncPinchFromPointers();
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      if (altMirrorActive && e.pointerType === 'mouse') {
        pointers.clear();
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
        finishPinch();
        return;
      }

      if (!pointers.has(e.pointerId)) return;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) finishPinch();
    };

    const touchesToPoints = (touches: TouchList): Pt[] => {
      const pts: Pt[] = [];
      for (let i = 0; i < touches.length; i += 1) {
        const t = touches.item(i);
        if (t) pts.push({ x: t.clientX, y: t.clientY });
      }
      return pts;
    };

    const onTouchStart = (e: TouchEvent) => {
      if (!optionsRef.current.enabled || e.touches.length < 2) return;
      // Gerçek / Simulator multi-touch → Alt mirror’ı bırak
      altMirrorActive = false;
      const pts = touchesToPoints(e.touches);
      if (pts.length < 2) return;
      e.preventDefault();
      if (!isPinchingRef.current) beginPinch(pts[0], pts[1]);
      else applyFromPoints(pts[0], pts[1]);
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!optionsRef.current.enabled || !isPinchingRef.current || e.touches.length < 2) {
        return;
      }
      const pts = touchesToPoints(e.touches);
      if (pts.length < 2) return;
      e.preventDefault();
      applyFromPoints(pts[0], pts[1]);
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length >= 2) return;
      if (e.touches.length === 0) pointers.clear();
      finishPinch();
    };

    const blockSafariPageZoom = (e: Event) => {
      e.preventDefault();
    };

    const opts = { passive: false, capture: true } as const;

    el.addEventListener('pointerdown', onPointerDown, opts);
    el.addEventListener('pointermove', onPointerMove, opts);
    el.addEventListener('pointerup', onPointerUp, opts);
    el.addEventListener('pointercancel', onPointerUp, opts);
    el.addEventListener('touchstart', onTouchStart, opts);
    el.addEventListener('touchmove', onTouchMove, opts);
    el.addEventListener('touchend', onTouchEnd, opts);
    el.addEventListener('touchcancel', onTouchEnd, opts);
    el.addEventListener('gesturestart', blockSafariPageZoom, opts);
    el.addEventListener('gesturechange', blockSafariPageZoom, opts);
    el.addEventListener('gestureend', blockSafariPageZoom, opts);

    return () => {
      el.removeEventListener('pointerdown', onPointerDown, opts);
      el.removeEventListener('pointermove', onPointerMove, opts);
      el.removeEventListener('pointerup', onPointerUp, opts);
      el.removeEventListener('pointercancel', onPointerUp, opts);
      el.removeEventListener('touchstart', onTouchStart, opts);
      el.removeEventListener('touchmove', onTouchMove, opts);
      el.removeEventListener('touchend', onTouchEnd, opts);
      el.removeEventListener('touchcancel', onTouchEnd, opts);
      el.removeEventListener('gesturestart', blockSafariPageZoom, opts);
      el.removeEventListener('gesturechange', blockSafariPageZoom, opts);
      el.removeEventListener('gestureend', blockSafariPageZoom, opts);
      pointers.clear();
      pinchRef.current = null;
      isPinchingRef.current = false;
      altMirrorActive = false;
    };
  }, [elementRef, enabled, mountKey]);

  return { isPinchingRef };
}
